











































import type { PropType, Ref } from '@vue/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import { IconSize, ButtonType } from '@getaccept/lib-ui';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  InputDocNode,
  ParagraphNode,
  MergeTagNode,
} from '@getaccept/editor-lib';
import { DateTime } from 'luxon';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import Text from '@tiptap/extension-text';
import Focus from '@tiptap/extension-focus';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import { getLocaleFromCountryCode, t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import { until } from '@vueuse/core';
import type Vue from 'vue';

export default defineComponent({
  components: {
    EditorContent,
  },
  props: {
    hasUpdateError: { type: Boolean },
    fieldFocused: { type: Boolean },
    inputField: { type: Object as PropType<Field>, required: true },
    recipientInput: { type: Object as PropType<EditorRecipientInput>, default: null },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    editable: { type: Boolean },
    isUpdating: { type: Boolean },
  },
  setup(props, { emit }) {
    const dateInputBoxRef = ref<Vue>(null);
    const closeOnAction = ref(false);

    const editor: Ref<TipTapEditor | null> = ref(null);

    const locale = computed(() => getLocaleFromCountryCode(props.mergeValues?.entity?.countryCode));
    const inputValue = computed(() => props.recipientInput?.value || props.inputField.value);
    const datePickerValue = computed(() => DateHelper.fromMillisToISO(+inputValue.value));

    const formattedDate = computed(() =>
      ProsemirrorHelper.getInputContent(
        inputValue.value
          ? DateHelper.fromMillisToMediumDate(+inputValue.value, locale.value)
          : t('input_fields_add_date'),
        props.fields
      )
    );
    const editorConfig = computed(() => ({
      content: formattedDate.value,
      editable: false,
      enableInputRules: [MergeTagNode],
      enablePasteRules: false,
      extensions: [InputDocNode, ParagraphNode, Text, Focus],
    }));

    const update = (newDate: string) => {
      const shouldUpdate = inputValue.value !== newDate;

      if (!shouldUpdate) {
        return;
      }

      emit('update', newDate);
    };

    const handleDateSelect = (date: Date) => {
      closeOnAction.value = true;
      const newDate = date ? DateTime.fromJSDate(date).toMillis().toString() : '';
      update(newDate);
    };

    const focus = () => {
      if (dateInputBoxRef.value) {
        (dateInputBoxRef.value.$el as HTMLElement).focus();
      }
    };

    watch(
      () => props.fieldFocused,
      () => {
        if (props.fieldFocused) {
          focus();
        }
      }
    );

    watch(formattedDate, () => {
      if (editor.value) {
        editor.value.commands.setContent(formattedDate.value);
      }
    });

    watch(
      () => props.editable,
      () => {
        editor.value.setEditable(props.editable);
      }
    );

    onMounted(async () => {
      editor.value = new TipTapEditor(editorConfig.value);

      if (props.fieldFocused) {
        await until(!!dateInputBoxRef.value).toBeTruthy();
        focus();
      }
    });

    onBeforeUnmount(() => {
      if (editor.value) {
        editor.value.destroy();
      }
    });

    return {
      ButtonType,
      IconSize,
      editor,
      update,
      datePickerValue,
      handleDateSelect,
      t,
      dateInputBoxRef,
      closeOnAction,
    };
  },
});
